import React from 'react';
import { CommonControlProps, IntegratedFormControl } from './integrated-form-control';
import { FieldPath, FieldValues } from 'react-hook-form';
import { Input, Text, css, styled, cssVar } from '@cad-arc';
import { genericMemo } from '../../common/helpers/memo';
import { useIntegratedForm } from '../use-integrated-form';

type InputProps = React.ComponentProps<typeof Input>;
export type FormInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = CommonControlProps<TFieldValues, TName> & {
    inputProps?: Partial<InputProps>;
    excludeFromFuzzySearch?: true;
};

// Adds style to the base input component instead of the input group wrapper
const StyledInput = styled(Input)<{
  $showFuzzyColor: boolean;
}>`
  ${({ $showFuzzyColor }) => $showFuzzyColor ? css`
    .arc-input-base{
      background-color: ${cssVar('arc.colors.brand.accent')};
    }
  ` : ''}
`;

export const FormInput = genericMemo(function FormInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: FormInputProps<TFieldValues, TName>): React.ReactElement {
    const integratedForm = useIntegratedForm();
    const {
        inputProps,
        renderViewMode,
        excludeFromFuzzySearch,
        ...integratedFormControlProps
    } = props;
    const inputPropsMemo = React.useMemo(() => inputProps ?? {}, [inputProps]);
    const renderViewModeToUse = React.useCallback(
        (value: string) => (renderViewMode ? renderViewMode(value) : <Text>{value}</Text>),
        [renderViewMode]
    );
    return (
        <IntegratedFormControl<string>
            {...integratedFormControlProps}
            renderViewMode={renderViewModeToUse}
        >
            {(providedProps) => (
                <StyledInput
                    {...providedProps}
                    {...inputPropsMemo}
                    value={providedProps?.value ?? inputPropsMemo?.value ?? ''}
                    $showFuzzyColor={Boolean(integratedForm.fuzzyMatchingEnabled) && !excludeFromFuzzySearch}
                />
            )}
        </IntegratedFormControl>
    );
});
